import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Grid, Box, Hidden, useMediaQuery, useTheme } from "@material-ui/core";
import {
  Layout,
  Container,
  MainSection,
  Questions,
  Curve,
  DarkSection,
  Image
} from "components";
import ArrowRightAltIcon from "@material-ui/icons/ArrowRightAlt";
import { throttle } from "lodash";
import clsx from "clsx";

import content from "_constants/content.json";
import "./styles.sass";

function GetStarted(layoutProps: any): React.ReactElement {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.only("sm"));

  const downloadNodeRef = useRef(null);
  const [downloadNodeVisible, setDownloadNodeVisible] = useState(false);
  const [downloadNodeScrolledUp, setDownloadNodeScrolledUp] = useState(false);
  const getDownloadNodePositionInViewport = (
    startOffset = 0,
    endOffset = 0
  ) => {
    let visible = false;
    let up = false;
    if (downloadNodeRef) {
      const downloadNodeElement = (downloadNodeRef.current as unknown) as HTMLElement;
      if (downloadNodeElement) {
        const top = downloadNodeElement.getBoundingClientRect().top;
        const bottom = downloadNodeElement.getBoundingClientRect().bottom;
        visible =
          top + startOffset <= 0 && bottom + endOffset >= window.innerHeight;
        up = bottom + endOffset < window.innerHeight;
      }
    }
    return [up, visible];
  };

  useEffect(() => {
    const handleScroll = () => {
      const [up, visible] = getDownloadNodePositionInViewport(-100, 0);
      setDownloadNodeVisible(visible);
      setDownloadNodeScrolledUp(up);
    };

    const throttledListener = throttle(handleScroll, 100);
    window.addEventListener("scroll", throttledListener);

    return () => window.removeEventListener("scroll", throttledListener);
  }, [downloadNodeVisible]);

  const stickyBanner = (show: boolean) => {
    return (
      <div className={clsx(
        "sticky-banner",
        show && "show"
      )}>
        <Container className="mobile-app-section">
          <div className="content">
            <Grid container className="content-grid">
              <Grid item xs={12} sm={5} md={3}>
                <p className="text">Ready to switch?</p>
              </Grid>
              <Grid
                item
                xs={12}
                sm={7}
                md={4}
                className="store"
                container
                spacing={1}
                alignItems="center"
                alignContent="center"
              >
                <Grid item>
                  <a
                    href={content.store.appStore}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Image
                      src="/images/badges/badge-appstore.png"
                      className="img"
                      alt="App Store"
                    />
                  </a>
                </Grid>
                <Grid item>
                  <Image
                    src="/images/badges/badge-googleplay.png"
                    className="img"
                    alt="Google Play"
                  />
                </Grid>
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>
    );
  };

  return (
    <Layout className="page-get-started" {...layoutProps}>
      <MainSection
        title={
          <span>
            Getting started is <br className="text-mobile-width-breakpoint" />{" "}
            quick and <br className="text-desktop-width-breakpoint" /> easy with{" "}
            <br className="text-mobile-width-breakpoint" />
            the free Mobi app.
          </span>
        }
        text={
          <span>
            We&lsquo;ll walk you through switching to Mobi step by step{" "}
            <br className="text-desktop-width-breakpoint" /> from your
            iPhone or Android — and it only takes a few minutes. 
          </span>
        }
        image="/images/img-getstarted.png"
      />
      <DarkSection>
        <div className="download-section" ref={downloadNodeRef}>
          <Container>
            <div className="header">
              <h2 className="title">How the app works</h2>
              <p className="description">
                The new Mobi app will be available through
                <br className="text-desktop-width-breakpoint" /> the Apple Store and Google Play.
              </p>
              <div
                className={clsx(
                  "store",
                  (downloadNodeVisible || downloadNodeScrolledUp) && "hide"
                )}
              >
                <Grid
                  container
                  alignItems="center"
                  justify="center"
                  spacing={2}
                >
                  <Grid item>
                    <a
                      href={content.store.appStore}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Image
                        src="/images/badges/badge-appstore.png"
                        className="badge"
                        alt="App Store"
                      />
                    </a>
                  </Grid>
                  <Grid item>
                    <Image
                      src="/images/badges/badge-googleplay.png"
                      className="badge"
                      alt="Google Play"
                    />
                  </Grid>
                </Grid>
              </div>
            </div>
            <div className="content">
              <Grid
                container
                justify="center"
                alignItems="center"
                className="item"
              >
                <Grid item xs={12} sm={6} className="left">
                  <h2 className="title">
                    Purchase your SIM card
                    <br className="text-desktop-width-breakpoint" /> through
                    the app.
                  </h2>
                  <p className="description">
                    Your purchase includes your first month of service on the ‘Mobi Plan’ valued at $9.99.
                  </p>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  container
                  direction="row"
                  justify="flex-end"
                >
                  <Box width={{ xs: 1, sm: 9 / 10, md: 4 / 5 }} className="img">
                    <Hidden only="xs">
                      <Image
                        src="/images/img-sim.png"
                        alt="Purchase a Mobi SIM card for $9.99."
                      />
                    </Hidden>
                    <Hidden smUp>
                      <Image
                        src="/images/img-sim-mobile.png"
                        alt="Purchase a Mobi SIM card for $9.99."
                      />
                    </Hidden>
                  </Box>
                </Grid>
              </Grid>
              <Grid
                container
                justify="center"
                alignItems="center"
                className="item"
              >
                <Grid item xs={12} sm={6}>
                  <Box width={{ xs: 1, sm: 9 / 10, md: 4 / 5 }} className="img">
                    <Image
                      src="/images/img-activate.png"
                      alt="Activate your SIM card."
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} className="text-left">
                  <h2 className="title">Activate your SIM card.</h2>
                  <p className="description">
                    When your SIM card arrives, activate
                    <br className="text-desktop-width-breakpoint" />
                    it though the app and keep your
                    <br className="text-desktop-width-breakpoint" /> number or
                    get a new one.
                  </p>
                </Grid>
              </Grid>
              <Grid
                container
                justify="center"
                alignItems="center"
                className="item"
              >
                <Grid item xs={12} sm={6} className="left">
                  <h2 className="title">Start saving on mobile.</h2>
                  <p className="description">
                    Once you switch to Mobi, you start saving. 
                    <br className="text-desktop-width-breakpoint" />
                    It's that simple.
                  </p>
                </Grid>
                <Grid item xs={12} sm={6} className="text-right">
                  <Box width={{ xs: 1, sm: 8 / 10, md: 4 / 5 }} className="img">
                    <Image
                      src="/images/img-saving.png"
                      alt="Start saving on mobile."
                    />
                  </Box>
                </Grid>
              </Grid>
            </div>
          </Container>
        </div>
      </DarkSection>

      <Hidden only="xs">
        {stickyBanner(downloadNodeVisible || downloadNodeScrolledUp)}
      </Hidden>

      <Hidden smUp>
        <Curve reverse={true} />
      </Hidden>

      <Container>
        <div className="faq">
          <Questions
            items={[
              {
                question: "What do I need to get started?",
                answer: "Youʻll just need: 1.) Wi-Fi or a current cellular connection to download the Mobi app and get started; 2.) a device thatʻs compatible with the Mobi network; 3.) a valid credit or debit card (with a U.S.-based billing address); and 4.) a few minutes to switch over."
              },
              {
                question: "How long does the activation take?",
                answer: "If you have an eSIM-capable device, we can get you activated with an eSIM and have your services up and running within minutes, or it only takes less than a minute to order your SIM and then a few days to receive it. Once you receive your SIM, it takes another few minutes to activate and get things up and running. If you're porting a number, it might take anywhere from a few minutes to a few days to fully move over (which might mean your old SIM continues to receive incoming calls, even though your Mobi SIM can make calls, send texts, and use data — in industry lingo, this is sometimes called 'dual service')."
              },
              {
                question: "What coverage can I expect?",
                answer: "You can check your coverage by contacting our care team (stay tuned for a much better coverage check tool, coming soon)."
              },
            ]}
          />
        </div>
      </Container>

      <div className="support-section">
        <Container disableGutters={isTablet}>
          <Grid container alignItems="center">
            <Grid item xs={12} sm={7} md={5}>
              <div className="support-sectionContent">
                <h3 className="support-sectionContent__title">
                  Have questions or need a little help getting started?
                </h3>
                <p className="support-sectionContent__text">
                  Anyone from our team would be happy to help you out over phone or email.
                </p>
                <Link to="help">
                  <p className="support-sectionContent__link flex-row">
                    Contact our support team <ArrowRightAltIcon />
                  </p>
                </Link>
              </div>
            </Grid>
            <Hidden mdUp xsDown>
              <Grid item sm={5}>
                <div className="support-section-mobile-image">
                  <Image src="/images/img-support-tablet.jpg" alt="Support" />
                </div>
              </Grid>
            </Hidden>
            <Hidden smDown>
              <Grid item sm={7}>
                <div className="support-section__image">
                  <Image
                    src="/images/img-support.jpeg"
                    responsive={false}
                    alt="Support"
                  />
                </div>
              </Grid>
            </Hidden>
          </Grid>
        </Container>
      </div>
      <Hidden smUp>
        <div className="support-section-mobile-image">
          <Image src="/images/img-support-mobile.png" alt="Support" />
        </div>
      </Hidden>

      <Hidden smUp>
        {stickyBanner(downloadNodeVisible)}
      </Hidden>
    </Layout>
  );
}

export default GetStarted;
